import './react-grid-styles.css';

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Responsive } from 'react-grid-layout';
import { map, range } from 'lodash';
import { withStyles } from '@material-ui/core/styles';

import CanvasHeader from './canvas-header';
import CustomWidthProvider from '../../common/components/custom-width-provider';
import WidgetContainer from '../containers/widget-container';

import {
    CANVAS_WIDGET_MARGINS,
    REACT_GRID_BREAKPOINTS,
    REACT_GRID_LAYOUT,
    REACT_GRID_ROW_HEIGHT,
    RIGHT_PANE_WIDTH
} from '../../common/constants/ui-options';

const ReactGridLayout = CustomWidthProvider( Responsive );

const styles = theme => ( {
    canvasContent: {
        backgroundColor: theme.palette.background.paper,
        flex: 1,
    },
    editMode: {
        marginLeft: `-${theme.spacing.unit * 2}px`,
        marginRight: RIGHT_PANE_WIDTH,
        paddingTop: theme.spacing.unit * 6
    },
    marginFix: {
        marginLeft: `-${theme.spacing.unit * 2}px`,
        marginRight: `-${theme.spacing.unit * 2}px`
    },
} );

export class Canvas extends Component{
    constructor( props ){
        super( props );
        this.state = {
            compactType: 'vertical',
            currentBreakpoint: 'lg',
            layouts: { lg: this.generateLayout() },
        };
    };

    onBreakpointChange = breakpoint => {
        this.onNewLayout();
        this.setState( {
            currentBreakpoint: breakpoint
        } );
    };

    onLayoutChange = ( layout, layouts ) => this.props.onLayoutChange( layout, layouts );

    onNewLayout(){
        this.setState( {
            layouts: { lg: this.generateLayout() }
        } );
    };

    generateLayout(){
        const { widgets } = this.props.canvasDetails;
        const total = widgets.length;
        return map( range( 0, total ), function( i ){
            const grid = widgets[i].dataGrid;
            const data = widgets[i];
            return {
                h: grid.h,
                i: i.toString(),
                info: {
                    exportable: data.exportable,
                    id: data.id,
                    name: data.name,
                    type: data.type,
                },
                maxH: grid.maxH,
                maxW: grid.maxW,
                minH: grid.minH,
                minW: grid.minW,
                w: grid.w,
                x: grid.x,
                y: grid.y,
            };
        } );
    };

    getWidgets = () => {
        const { canvasDetails = {} } = this.props;
        return map( this.state.layouts.lg, function( l, i ){
            const widgetDetails = l.info;
            return (
                <div key={i} className={l.static ? 'static' : null}>
                    <WidgetContainer
                        widgetDetails={widgetDetails}
                        id={widgetDetails.id}
                        canvasDataParams={canvasDetails.params}
                    />
                </div>
            );
        } );
    };

    onResizeStop = () => {
        this.setState( ( state ) => {
            return { resizeToggle: !state.resizeToggle };
        } );
    }

    render(){
        const {
            canvasDetails,
            classes,
            handleCloseSettingsPane,
            handleOpenSettingsPane,
            handleSaveSettings,
            isEditMode,
            isFormDirty
        } = this.props;
        const { compactType, layouts } = this.state;

        return (
            <div classes={classes.canvasContent}>
                <CanvasHeader
                    canvasDetails={canvasDetails}
                    handleCloseSettingsPane={handleCloseSettingsPane}
                    handleOpenSettingsPane={handleOpenSettingsPane}
                    handleSaveSettings={handleSaveSettings}
                    isEditMode={isEditMode}
                    isFormDirty={isFormDirty}
                />
                <ReactGridLayout
                    breakpoints={REACT_GRID_BREAKPOINTS}
                    className={cx( {
                        [classes.editMode]: isEditMode,
                        [classes.marginFix]: !isEditMode,
                    } )}
                    cols={REACT_GRID_LAYOUT}
                    compactType={compactType}
                    isDraggable={isEditMode}
                    isResizable={isEditMode}
                    layouts={layouts}
                    margin={CANVAS_WIDGET_MARGINS}
                    measureBeforeMount={false}
                    onBreakpointChange={this.onBreakpointChange}
                    onLayoutChange={this.onLayoutChange}
                    onResizeStop={this.onResizeStop}
                    rowHeight={REACT_GRID_ROW_HEIGHT}
                    preventCollision={!compactType}
                >
                    {this.getWidgets()}
                </ReactGridLayout>
            </div>
        );
    }
};

Canvas.defaultProps = {
    onLayoutChange: () => {}
};

Canvas.propTypes = {
    canvasDetails: PropTypes.object.isRequired,
    classes: PropTypes.object.isRequired,
    content: PropTypes.object,
    handleCloseSettingsPane: PropTypes.func.isRequired,
    handleOpenSettingsPane: PropTypes.func.isRequired,
    handleSaveSettings: PropTypes.func.isRequired,
    isEditMode: PropTypes.bool.isRequired,
    isFormDirty: PropTypes.bool.isRequired,
    onLayoutChange: PropTypes.func.isRequired,
};

export default withStyles( styles )( Canvas );
